import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormField, FormItem, FormLabel, FormMessage } from "~/atoms/form";
import { UserStatus, PlatformRole } from "~/config/types";
import { type User } from "~/api/types";
import { type DocumentEditFormValues } from "~/hooks/useDocumentEditForm";
import useUsers from "~/api/swr/useUsers";
import { printFullName } from "~/helpers/user";
import { useUserContext } from "~/hooks/useUserContext";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";

type ManagersFieldMode = "all" | "ownerId" | "publisherId";

interface DocumentManagersFieldsProps {
  mode?: ManagersFieldMode;
}

const isUserValidForOwnershipPublisher = (categoryId: string, user: User) => {
  if (!categoryId) return true;
  if (user.role === PlatformRole.reader) return false;
  if (user.role === PlatformRole.contributor) {
    return user.categories.some(
      (category) => category.id === Number(categoryId),
    );
  }
  return true;
};

export const DocumentManagersFields = ({
  mode = "all",
}: DocumentManagersFieldsProps) => {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();
  const form = useFormContext<DocumentEditFormValues>();

  const { data: users } = useUsers({
    status: [UserStatus.active, UserStatus.pending],
    roles: [
      PlatformRole.contributor,
      PlatformRole.coordinator,
      PlatformRole.superAdmin,
    ],
    putCurrentUserAtTop: true,
  });

  const userOptions = users?.map((user) => ({
    value: user.id.toString(),
    label: printFullName(user, user.id === currentUser.id),
  }));

  const [ownerId, publisherId, categoryId] = useWatch({
    control: form.control,
    name: ["ownerId", "publisherId", "categoryId"],
  });

  const ownerUser = users?.find((user) => user.id === Number(ownerId)) ?? false;
  const publisherUser =
    users?.find((user) => user.id === Number(publisherId)) ?? false;

  const showOwnerField = mode === "all" || mode === "ownerId";
  const showPublisherField = mode === "all" || mode === "publisherId";

  return (
    <div className="flex flex-col gap-2">
      {showOwnerField && (
        <FormField
          control={form.control}
          name="ownerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("commons.document.owner")}</FormLabel>
              <ControlledCombobox
                value={field.value}
                options={userOptions ?? []}
                placeholder={t("commons.customSelect.enterOwner")}
                onChange={field.onChange}
              />
              <FormMessage>
                {ownerUser &&
                  !isUserValidForOwnershipPublisher(categoryId, ownerUser) &&
                  t("validations.contributorOwnerMustBeInCategory")}
              </FormMessage>
            </FormItem>
          )}
        />
      )}
      {showPublisherField && (
        <FormField
          control={form.control}
          name="publisherId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("commons.document.publisher")}</FormLabel>
              <ControlledCombobox
                value={field.value}
                options={userOptions ?? []}
                placeholder={t("commons.customSelect.enterPublisher")}
                onChange={field.onChange}
              />
              <FormMessage>
                {publisherUser &&
                  !isUserValidForOwnershipPublisher(
                    categoryId,
                    publisherUser,
                  ) &&
                  t("validations.contributorPublisherMustBeInCategory")}
              </FormMessage>
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
